import React, { useState, useEffect, useReducer, useContext, useCallback } from 'react';

import { useQuery, useSubscription } from '@apollo/client';

import GeneralLayout from '../../../../layouts/General';
import { useNavigate } from 'react-router';
import { AuthContext } from '../../../../components/AuthProvider';
import { wordsToAcronym } from '../../../../utils/stringUtils';
import CustomBackdrop from '../../../../components/CustomBackdrop';
import Pathway, { PathwayStep, EnumPathwayStepComopnentType } from '../../../../components/PathwayBuilder/Common';

import Steps from '../Common/components/Steps';

import { patientReducer } from '../Common/patient';
import { specialityCategoriesReducer } from '../Common/speciality';
import { specialityPathwayReducer } from '../Common/speciality_pathway';
import { potsReducer } from '../Common/pots';
import { Organisation } from '../Common/organisation';
import { Lab } from '../Common/lab';

import { getPathwaysQuery, createCaseInitQuery, Speciality, LabAssignment, getExistingDataIds } from './queries';
import { createCase, createCaseEvent, createPotPathwayVersion, updateCaseEvent } from './mutations';
import { UnknownObject } from '../../../../react-app-env';
import { _newCaseEventSubscription } from './subscriptions';
import { webhookClient } from '../../../../utils/webhookClient';
import { UploadItem } from '../../../../utils/types';

const CreateCase = (): React.ReactElement => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext)!;
  const [accessionNumber, setAccessionNumber] = useState<string | undefined>(undefined);
  const [newCaseEventId, setNewCaseEventId] = useState('');

  const { data: newCaseEvent } = useSubscription(_newCaseEventSubscription(newCaseEventId));

  const [patient, dispatchPatient] = useReducer(patientReducer, {
    nhs_number: '',
    mrn: '',
    first_name: '',
    last_name: '',
    dob: null,
    gender: '',
  });

  const [specialityCategories, dispatchSpecialityCategories] = useReducer(specialityCategoriesReducer, []);

  const [specialityPathwayValues, dispatchSpecialityPathwayValues] = useReducer(specialityPathwayReducer, {});

  const [pots, dispatchPots] = useReducer(potsReducer, {
    pots: [],
  });

  const [specialityPathway, setSpecialityPathway] = useState<Record<string, Pathway>>({});
  const [potPathway, setPotPathway] = useState<Record<string, Pathway>>({});

  const [saving, setSaving] = useState<boolean>(false);
  const [organisation, setOrganisation] = useState<Organisation>();
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [lab, setLab] = useState<Lab>();
  const [labs, setLabs] = useState<Lab[]>([]);
  const [specialities, setSpecialities] = useState<Speciality[]>([]);

  const [files, setFiles] = useState<UploadItem[]>([]);

  const { data: createCaseInitData } = useQuery(createCaseInitQuery, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    let mounted = true;
    if (mounted && createCaseInitData) {
      const orgs = createCaseInitData.organisations;
      setOrganisations(orgs);
      if (orgs.length === 1) {
        setOrganisation(orgs[0]);
      }
      const lbs = createCaseInitData.organisation_lab_assignment.map((i: LabAssignment) => ({ ...i.lab, organisation_id: i.organisation_id }));
      setLabs(lbs);
      if (lbs.length === 1) {
        setLab(lbs[0]);
      }
      setSpecialities(createCaseInitData.specialities);
    }
    return () => {
      mounted = false;
    };
  }, [createCaseInitData]);

  useEffect(() => {
    let mounted = true;
    if (mounted && newCaseEvent) {
      const accession_number = newCaseEvent.case_events_by_pk.accession_number;
      if (accession_number) {
        setAccessionNumber(accession_number);
      }
    }
    return () => {
      mounted = false;
    };
  }, [newCaseEvent]);

  const handleSave = async () => {
    setSaving(true);

    const organisation_id = organisation?.id;
    const lab_id = lab?.id;

    const { a_patient_id, a_surgical_pathway_version_ids, a_pot_pathway_version_ids } = await getExistingDataIds(patient.nhs_number, specialityPathway, potPathway);

    const pot_pathway_version_ids: Record<string, string> = {};

    // Loop through each potPathway (by category)
    for (const [categoryId, pot] of Object.entries(potPathway)) {
      const existingId = a_pot_pathway_version_ids[categoryId];

      if (existingId) {
        pot_pathway_version_ids[categoryId] = existingId;
      } else {
        const insertPotPathwayVersionVariables = {
          object: {
            data: {
              name: pot.name,
              steps: pot.steps,
            },
            pot_pathway_id: pot.id,
            version_timestamp: pot.version_timestamp,
          },
        };
        const newVersionId = await createPotPathwayVersion(insertPotPathwayVersionVariables);
        pot_pathway_version_ids[categoryId] = newVersionId;
      }
    }

    let accession_id_prefix = `${wordsToAcronym(`${organisation?.name}`)}`;
    if (accession_id_prefix.length === 1) {
      accession_id_prefix = `${organisation?.name.toLocaleUpperCase().substring(0, 3)}`;
    }
    // const assession__org_code = generateNumberUuid(2);
    // const assession_lab_code = generateNumberUuid(6);
    // const accession_id = `${accession_id_prefix}${assession__org_code}-${assession_lab_code}`;

    const insertCaseVariables: UnknownObject = {
      object: {
        organisation_id,
        lab_id,
        pots: {
          data: pots.pots.map((pot) => ({
            organisation_id,
            lab_id,
            speciality_category_id: pot.speciality_category_id,
            name: pot.name,
            pathway: potPathway[pot.speciality_category_id]?.steps
              .map((p: PathwayStep) => ({
                id: p.id,
                title: p.name,
                label: p.options.find((o) => o.id === pot.pathway[p.id])?.label,
                value: pot.pathway[p.id],
              }))
              .filter((i) => i.value),
            note: pot.notes,
            created_by: user?.id,
            pot_pathway_version_id: pot_pathway_version_ids[pot.speciality_category_id],
            specimen_qualifier: potPathway[pot.speciality_category_id]?.steps.find((i) => i.specimen_qualifier)?.id ?? '',
          })),
        },
        mrn: patient.mrn,
        accession_id: accessionNumber,
        surgical_pathway_versions: {
          data: Object.entries(specialityPathway).map(([categoryId, pathway]) => {
            const steps = specialityPathwayValues[categoryId]?.pathway || {};
            const mappedSteps = pathway?.steps
              .map((p: PathwayStep) => {
                const stepValue = steps[p.id];
                const isFreetext = p.component_type === EnumPathwayStepComopnentType.FREETEXT;
                return {
                  id: p.id,
                  title: p.name,
                  label: isFreetext ? stepValue : p.options.find((o) => o.id === stepValue)?.label,
                  value: stepValue,
                  component_type: p.component_type,
                };
              })
              .filter((i) => i.value);

            const existingVersionId = a_surgical_pathway_version_ids[categoryId];

            return {
              note: specialityPathwayValues[categoryId]?.notes,
              speciality_category_id: categoryId,
              surgical_pathways: mappedSteps,

              ...(existingVersionId
                ? {
                    surgical_pathway_version_id: existingVersionId,
                  }
                : {
                    surgical_pathway_version: {
                      data: {
                        surgical_pathway_id: pathway.id,
                        version_timestamp: pathway.version_timestamp,
                        data: {
                          name: pathway.name,
                          steps: pathway.steps,
                        },
                      },
                    },
                  }),
            };
          }),
        },
        created_by: user?.id,
      },
    };

    if (!a_patient_id) {
      insertCaseVariables.object.patient = {
        data: {
          nhs_id: patient.nhs_number,
          dob: patient.dob,
        },
      };
    } else {
      insertCaseVariables.object.patient_id = a_patient_id;
    }

    const case_id: string | undefined = await createCase(insertCaseVariables);
    if (case_id) {
      await handleUpdateNewCaseEvent(case_id);
      await handleCreateFullCaseEvent(case_id);

      if (files.length) {
        await uploadFiles(case_id);
      }

      setSaving(false);
      handleFinish(case_id);
    }
  };

  const uploadFiles = async (caseid: string) => {
    const formData = new FormData();
    const uploadableFiles = (files as File[]).filter((f) => f instanceof File);
    if (!uploadableFiles.length) return;

    uploadableFiles.forEach((file) => formData.append('files', file));
    await webhookClient.postFormData(`cases/${caseid}/upload-document`, formData);
  };

  const handleCreateNewCaseEvent = useCallback(async () => {
    const caseEventId = await createCaseEvent({
      object: {
        event_type: 'NEW_CASE',
        organisation_id: organisation?.id,
        event_data: {
          dob: patient.dob,
          last_name: patient.last_name,
          first_name: patient.first_name,
          nhs_number: patient.nhs_number,
        },
      },
    });
    setNewCaseEventId(caseEventId!);
  }, [organisation, patient]);

  const handleUpdateNewCaseEvent = useCallback(
    async (case_id: string) => {
      const caseEventId = await updateCaseEvent({
        pk_columns: {
          id: newCaseEventId,
        },
        set: {
          case_id,
        },
      });
      setNewCaseEventId(caseEventId!);
    },
    [newCaseEventId]
  );

  const handleCreateFullCaseEvent = useCallback(
    async (case_id: string) => {
      await createCaseEvent({
        object: {
          event_type: 'CASE_DATA',
          organisation_id: organisation?.id,
          accession_number: accessionNumber ?? null,
          case_id,
          event_data: {
            patient: {
              dob: patient.dob,
              last_name: patient.last_name,
              first_name: patient.first_name,
              nhs_number: patient.nhs_number,
            },
            pots: pots.pots.map((pot) => ({
              name: pot.name,
              note: pot.notes,
              specimen_qualifier: potPathway[pot.speciality_category_id].steps.find((i) => i.specimen_qualifier)?.id ?? '',
            })),
            report: Object.entries(specialityPathway).flatMap(([categoryId, pathway]) =>
              pathway.steps
                .map((p: PathwayStep) => ({
                  title: p.name,
                  label: specialityPathwayValues[categoryId]?.pathway?.[p.id]
                    ? p.options.find((o) => o.id === specialityPathwayValues[categoryId].pathway[p.id])?.label
                    : undefined,
                }))
                .filter((i) => i.label)
            ),
          },
        },
      });
    },
    [accessionNumber, patient, potPathway, organisation, pots, specialityPathway, specialityPathwayValues]
  );

  const handleFinish = (id: string) => {
    navigate(`/org/cases/${id}`);
  };

  const handleCategoryChange = useCallback(
    async (category_ids: string[]) => {
      if (!accessionNumber) {
        handleCreateNewCaseEvent();
      }

      const res = await getPathwaysQuery(category_ids);

      // Group surgical pathways by category ID
      const surgicalGrouped = res.data.surgical_pathways.reduce(
        (acc, pathway) => {
          acc[pathway.speciality_category_id] = {
            ...pathway,
            steps: pathway.steps.map((step) => ({
              ...step,
              specimen_qualifier: '',
            })),
          };
          return acc;
        },
        {} as Record<string, Pathway>
      );

      // Group pot pathways by category ID
      const potGrouped = res.data.pot_pathways.reduce(
        (acc, pathway) => {
          acc[pathway.speciality_category_id] = pathway;
          return acc;
        },
        {} as Record<string, Pathway>
      );

      setSpecialityPathway(surgicalGrouped);
      setPotPathway(potGrouped);
    },
    [accessionNumber, handleCreateNewCaseEvent]
  );

  return (
    <>
      <GeneralLayout breadcrumbs={[{ label: 'Cases', link: '/org/cases' }, { label: 'Create' }]}>
        <Steps
          completeLabel="Create"
          files={files}
          patient={patient}
          specialities={specialities}
          specialityCategories={specialityCategories}
          specialityPathwayValues={specialityPathwayValues}
          accessionNumber={accessionNumber}
          pots={pots.pots}
          user={user}
          organisation={organisation}
          organisations={organisations}
          lab={lab}
          labs={labs}
          specialityPathway={specialityPathway}
          potPathway={potPathway}
          dispatchPatient={dispatchPatient}
          dispatchSpecialityCategories={dispatchSpecialityCategories}
          dispatchSpecialityPathwayValues={dispatchSpecialityPathwayValues}
          setOrganisation={setOrganisation}
          setFiles={setFiles}
          setLab={setLab}
          dispatchPots={dispatchPots}
          handleCategoryChange={handleCategoryChange}
          handleSave={handleSave}
        />
      </GeneralLayout>
      {saving && <CustomBackdrop label="Creating Case" />}
    </>
  );
};

export default CreateCase;
